import Dexie, { type Table } from 'dexie';
import type { Appointment, CampaignClient } from '@/types/appointments';
import type { CrmClient, CustomerStatsCacheItem } from '@/types/crm';

export interface CachedBookingDetail {
  bookingId: string;
  workspaceId: string;
  phone: string;
  email: string;
  customerName?: string;
  totalAmount?: number;
  salesOrderId?: string;
  salePaymentStatus?: string;
  updatedAt: number;
}

export interface AppMetaRecord {
  key: string;
  value: any;
  updatedAt: number;
}

export class FreshaHubDatabase extends Dexie {
  appointments!: Table<Appointment, string>;
  bookingDetails!: Table<CachedBookingDetail, string>;
  campaignClients!: Table<CampaignClient, string>;
  crmClients!: Table<CrmClient, string>;
  customerStats!: Table<CustomerStatsCacheItem & { customerId: string }, string>;
  appMeta!: Table<AppMetaRecord, string>;

  constructor() {
    super('FreshaHubDB');
    this.version(1).stores({
      appointments: 'id, date, [workspaceId+date], [workspaceId+date+locationId], status',
      bookingDetails: 'bookingId, workspaceId, updatedAt',
      campaignClients: 'id, customerId, workspaceId, segment, daysAbsent, totalSales, outstandingAmount',
      customerStats: 'customerId, lastFetch',
      appMeta: 'key, updatedAt',
    });
    this.version(2).stores({
      crmClients: 'id, customerId, workspaceId, segmentId, daysAbsent, totalSales, outstandingAmount',
    });
  }
}

export const db = new FreshaHubDatabase();

// Helper Data Access Functions

export async function getAppointmentsByDate(
  workspaceId: string,
  date: string,
  locationId?: string | null
): Promise<Appointment[]> {
  try {
    if (locationId && locationId !== 'all') {
      return await db.appointments
        .where('[workspaceId+date+locationId]')
        .equals([workspaceId, date, locationId])
        .toArray();
    }
    return await db.appointments
      .where('[workspaceId+date]')
      .equals([workspaceId, date])
      .toArray();
  } catch (err) {
    console.error('Error reading appointments from Dexie:', err);
    return [];
  }
}

export async function saveAppointments(appointments: Appointment[]): Promise<void> {
  if (!appointments || appointments.length === 0) return;
  try {
    await db.appointments.bulkPut(appointments);
  } catch (err) {
    console.error('Error saving appointments to Dexie:', err);
  }
}

export async function syncAppointmentsForDate(
  workspaceId: string,
  date: string,
  locationId: string | null | undefined,
  appointments: Appointment[]
): Promise<void> {
  try {
    await db.transaction('rw', db.appointments, async () => {
      if (locationId && locationId !== 'all') {
        await db.appointments
          .where('[workspaceId+date+locationId]')
          .equals([workspaceId, date, locationId])
          .delete();
      } else {
        await db.appointments
          .where('[workspaceId+date]')
          .equals([workspaceId, date])
          .delete();
      }
      if (appointments.length > 0) {
        await db.appointments.bulkPut(appointments);
      }
    });
  } catch (err) {
    console.error('Error syncing appointments for date in Dexie:', err);
  }
}

export async function getCachedBookingDetails(
  bookingIds: string[]
): Promise<Record<string, CachedBookingDetail>> {
  if (!bookingIds || bookingIds.length === 0) return {};
  try {
    const details = await db.bookingDetails.where('bookingId').anyOf(bookingIds).toArray();
    const map: Record<string, CachedBookingDetail> = {};
    for (const d of details) {
      map[d.bookingId] = d;
    }
    return map;
  } catch (err) {
    console.error('Error fetching cached booking details:', err);
    return {};
  }
}

export async function saveBookingDetails(details: CachedBookingDetail[]): Promise<void> {
  if (!details || details.length === 0) return;
  try {
    await db.bookingDetails.bulkPut(details);
  } catch (err) {
    console.error('Error saving booking details to Dexie:', err);
  }
}

export async function getCampaignClients(workspaceId: string): Promise<CampaignClient[]> {
  try {
    return await db.campaignClients.where('workspaceId').equals(workspaceId).toArray();
  } catch (err) {
    console.error('Error reading campaign clients from Dexie:', err);
    return [];
  }
}

export async function saveCampaignClients(clients: CampaignClient[]): Promise<void> {
  if (!clients || clients.length === 0) return;
  try {
    await db.campaignClients.bulkPut(clients);
  } catch (err) {
    console.error('Error saving campaign clients to Dexie:', err);
  }
}

export async function getCrmClients(
  workspaceId: string,
  segmentId?: string,
  memberships?: Record<string, string[]>
): Promise<CrmClient[]> {
  try {
    let collection;
    if (workspaceId) {
      collection = db.crmClients.where('workspaceId').equals(workspaceId);
    } else {
      collection = db.crmClients.toCollection();
    }
    if (segmentId && segmentId !== 'all') {
      return await collection
        .filter(
          (c) =>
            c.segmentId === segmentId ||
            Boolean(memberships && (memberships[segmentId]?.includes(c.id) || (c.customerId && memberships[segmentId]?.includes(c.customerId))))
        )
        .toArray();
    }
    return await collection.toArray();
  } catch (err) {
    console.error('Error reading CRM clients from Dexie:', err);
    return [];
  }
}

export async function saveCrmClients(clients: CrmClient[]): Promise<void> {
  if (!clients || clients.length === 0) return;
  try {
    await db.crmClients.bulkPut(clients);
  } catch (err) {
    console.error('Error saving CRM clients to Dexie:', err);
  }
}

export async function clearCrmClients(workspaceId?: string): Promise<void> {
  try {
    if (workspaceId) {
      await db.crmClients.where('workspaceId').equals(workspaceId).delete();
    } else {
      await db.crmClients.clear();
    }
  } catch (err) {
    console.error('Error clearing CRM clients from Dexie:', err);
  }
}


export async function getAllCustomerStats(): Promise<Record<string, CustomerStatsCacheItem>> {
  try {
    const all = await db.customerStats.toArray();
    const map: Record<string, CustomerStatsCacheItem> = {};
    for (const item of all) {
      map[item.customerId] = item;
    }
    return map;
  } catch (err) {
    console.error('Error loading customer stats from Dexie:', err);
    return {};
  }
}

export async function saveCustomerStatsBatch(
  items: (CustomerStatsCacheItem & { customerId: string })[]
): Promise<void> {
  if (!items || items.length === 0) return;
  try {
    await db.customerStats.bulkPut(items);
  } catch (err) {
    console.error('Error saving customer stats to Dexie:', err);
  }
}

export async function getAppMeta<T>(key: string): Promise<T | null> {
  try {
    const record = await db.appMeta.get(key);
    return record ? (record.value as T) : null;
  } catch (err) {
    console.error(`Error reading app meta key "${key}":`, err);
    return null;
  }
}

export async function setAppMeta<T>(key: string, value: T): Promise<void> {
  try {
    await db.appMeta.put({
      key,
      value,
      updatedAt: Date.now(),
    });
  } catch (err) {
    console.error(`Error writing app meta key "${key}":`, err);
  }
}
